/* eslint-disable react-refresh/only-export-components */
// src/context/LeadsContext.jsx
import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { SEED_LEADS } from '../components/leadhub/leadData';
import { fetchLeads, subscribeToNewLeads } from '../lib/leadsDb';

// ─── Constants ────────────────────────────────────────────────────────────────

export const MARKETING_SOURCES = ['facebook', 'instagram', 'google', 'yelp', 'website'];

const STORAGE_KEY    = 'wm-leads-v1';
const MKT_KEY        = 'wm-mkt-leads';
const MIGRATION_FLAG = 'wm-mkt-leads-migrated';

// ─── Storage helpers ──────────────────────────────────────────────────────────

function loadLeads() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return SEED_LEADS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return SEED_LEADS;
    return parsed;
  } catch {
    return SEED_LEADS;
  }
}

function saveLeads(leads) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(leads)); } catch { /* ignore */ }
}

// ─── One-time migration: wm-mkt-leads → wm-leads-v1 ─────────────────────────
// Runs once on first mount. Reads the old Marketing leads store, maps records to
// the LeadHub schema, merges any that aren't already in wm-leads-v1, then sets
// the migration flag so it never runs again.

function migrateMarketingLeads(existingLeads) {
  try {
    if (localStorage.getItem(MIGRATION_FLAG)) return existingLeads;

    const raw = localStorage.getItem(MKT_KEY);
    if (!raw) {
      localStorage.setItem(MIGRATION_FLAG, '1');
      return existingLeads;
    }
    const mktLeads = JSON.parse(raw);
    if (!Array.isArray(mktLeads) || mktLeads.length === 0) {
      localStorage.setItem(MIGRATION_FLAG, '1');
      return existingLeads;
    }

    const merged = [...existingLeads];
    mktLeads.forEach((m) => {
      // Skip if already in LeadHub (match by email, or name+phone)
      const alreadyExists =
        (m.email && merged.some(l => l.email?.toLowerCase() === m.email.toLowerCase())) ||
        (m.name && m.phone && merged.some(
          l => l.name?.toLowerCase() === m.name.toLowerCase() &&
               l.phone?.replace(/\D/g, '') === m.phone?.replace(/\D/g, '')
        ));
      if (alreadyExists) return;

      // Parse vehicle string e.g. "2022 Toyota Tacoma" → { year, make, model }
      const parts = (m.vehicle || '').trim().split(/\s+/);
      const year  = parts[0] && /^\d{4}$/.test(parts[0]) ? Number(parts[0]) : null;
      const make  = year ? (parts[1] || '') : '';
      const model = year ? parts.slice(2).join(' ') : (m.vehicle || '');

      // Map old Marketing statuses to LeadHub statuses
      const statusMap = { converted: 'won', new: 'new', contacted: 'contacted' };

      merged.push({
        id:              `mkt-migrated-${m.id || Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        name:            m.name || '',
        phone:           m.phone || '',
        email:           m.email || '',
        vehicle:         { year, make, model },
        serviceInterest: m.service || '',
        budget:          null,
        source:          (m.source || 'website').toLowerCase(),
        status:          statusMap[m.status] || 'new',
        priority:        'warm',
        assignee:        null,
        tags:            [],
        notes:           m.notes || '',
        followUpDate:    null,
        createdAt:       m.createdAt || new Date().toISOString(),
        lastContactedAt: null,
        activities:      [],
      });
    });

    localStorage.setItem(MIGRATION_FLAG, '1'); // ← set AFTER successful merge
    return merged;
  } catch {
    return existingLeads; // flag NOT set — allows retry on next mount
  }
}

// ─── Context ──────────────────────────────────────────────────────────────────

const LeadsContext = createContext(null);

export function LeadsProvider({ children }) {
  const { orgId } = useAuth();

  const [leads, setLeads] = useState(() => {
    const base = loadLeads();
    return migrateMarketingLeads(base);
  });
  const [realtimeStatus, setRealtimeStatus]         = useState('CONNECTING');
  const [latestRealtimeLead, setLatestRealtimeLead] = useState(null);

  // Persist to localStorage on every change
  useEffect(() => { saveLeads(leads); }, [leads]);

  // Supabase: fetch org-scoped leads on auth
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      try {
        const remote = await fetchLeads(orgId);
        if (!cancelled && remote.length > 0) setLeads(remote);
      } catch (err) {
        console.error('[LeadsContext] Supabase fetch failed, using localStorage:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  // Supabase Realtime subscription
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!orgId) {
      setRealtimeStatus('offline');
      return;
    }
    const unsubscribe = subscribeToNewLeads(
      (newLead) => {
        if (!newLead) return;
        setLeads(prev => {
          if (prev.some(l => l.id === newLead.id)) return prev;
          return [newLead, ...prev];
        });
        setLatestRealtimeLead(newLead); // LeadHubPage watches this to show a toast
      },
      (status) => setRealtimeStatus(status),
      orgId
    );
    return () => { try { unsubscribe?.(); } catch { /* ignore */ } };
  }, [orgId]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Derived: leads whose source is one of the 5 marketing channels
  const marketingLeads = useMemo(
    () => leads.filter(l => MARKETING_SOURCES.includes(l.source)),
    [leads]
  );

  return (
    <LeadsContext.Provider value={{ leads, setLeads, realtimeStatus, latestRealtimeLead, marketingLeads }}>
      {children}
    </LeadsContext.Provider>
  );
}

export function useLeads() {
  const ctx = useContext(LeadsContext);
  if (!ctx) throw new Error('useLeads must be used inside LeadsProvider');
  return ctx;
}
